import { FunctionMode, SelectableModel } from '@/types/message.type'
import { SearchFilter } from '@/types/filter.type'
import { Source } from '@/types/source'

export interface VaultSelections {
  uploads: string[]
  storage_documents: string[]
  storage_containers: string[]
}

// Sort options for the /recents list and the Cmd+K command palette.
// Mirrors the whitelist enforced server-side in ConversationService#index.
export type ConversationSortField = 'updated_at' | 'created_at'
export type ConversationSortOrder = 'desc' | 'asc'

export interface ConversationSort {
  sortBy: ConversationSortField
  order: ConversationSortOrder
}

export const DEFAULT_CONVERSATION_SORT: ConversationSort = {
  sortBy: 'updated_at',
  order: 'desc',
}

export interface Conversation {
  id: string
  name: string
  created_at: string
  updated_at: string
  status?: ConversationStatus
  filters?: SearchFilter
  isNameGenerated?: boolean
  start_model?: SelectableModel
  is_starred?: boolean
  sources?: Source[]
  onyx_chat_session_id?: string
  first_message_function_mode?: FunctionMode
  vault_selections?: VaultSelections
  folder_ids?: string[]
  // Full-text search match snippet (PRD #3323). Present only on /recents + Cmd+K
  // search responses where the body matched; carries U+E000/U+E001 highlight
  // sentinels (not HTML) — render via renderSearchSnippet (escape then <strong>).
  snippet?: string | null
}

// --- Conversation folders (v1.3) ---

export interface ConversationFolder {
  id: string
  name: string
  description: string | null
  is_starred: boolean
  conversations_count: number
  created_at: string
  updated_at: string
}

export interface ConversationFolderListResponse {
  data: ConversationFolder[]
  total: number
  page: number
  per_page: number
  has_next: boolean
  uncategorized_count: number
}

export interface CreateConversationFolderParams {
  name: string
  description?: string
}

export interface UpdateConversationFolderParams {
  id: string
  name?: string
  description?: string
  is_starred?: boolean
}

export type FolderMembershipOp = 'add' | 'remove'

export interface BulkFolderMembershipParams {
  folderId: string
  conversation_ids: string[]
  op: FolderMembershipOp
}

export interface BulkFolderMembershipResult {
  added: string[]
  removed: string[]
  skipped: string[]
  failed: string[]
}

// Sentinel for the "Uncategorized" pseudo-folder filter on the Recents page.
// Mirrors the server-side ConversationService::UNCATEGORIZED_FOLDER_ID.
export const UNCATEGORIZED_FOLDER_ID = 'none'

// Sentinel for the "Favorite" pseudo-filter — a rail item (not a real folder)
// that filters the list to starred conversations via the is_starred param.
export const FAVORITES_FILTER_ID = 'favorites'

// Max length for a conversation folder name (enforced on create + rename).
export const MAX_FOLDER_NAME_LENGTH = 30

// Max length for a folder description (mirrors server-side validation).
export const MAX_FOLDER_DESCRIPTION_LENGTH = 500

// --- AI folder organize (LIN-1164) ---

export interface OrganizeProposalFolder {
  name: string
  description: string | null
  existing_folder_id: string | null
  conversation_ids: string[]
}

export interface OrganizeProposal {
  folders: OrganizeProposalFolder[]
  // Lookup for every id referenced in `folders` so the modal can show names.
  conversations: { id: string; name: string }[]
  unassigned_count: number
  truncated: boolean
}

export interface OrganizeChatMessage {
  role: 'user' | 'assistant'
  content: string
}

// Client-side editable copy of a proposed folder.
export interface EditableProposalFolder {
  key: string
  name: string
  description: string | null
  existingFolderId: string | null
  conversationIds: string[]
  excludedIds: string[]
}

export interface UpdateConversationParams {
  id: string
  name?: string
  is_starred?: boolean
  filters?: SearchFilter | null
  status?: ConversationStatus
  start_model?: SelectableModel
  onyx_chat_session_id?: string
  vault_selections?: Partial<VaultSelections>
  remove_vault_selections?: Partial<VaultSelections>
}

export interface GenerateConversationNameParams {
  question: string
  answer: string
}

export enum ConversationStatus {
  Active = 'active',
  Temp = 'temp',
  Deleting = 'deleting',
  DeleteFailed = 'delete_failed',
}

export enum OnyxChatStatus {
  Pending = 'pending',
  Success = 'success',
  Failed = 'failed',
  Stopped = 'stopped',
}
export interface SendConversationSlackThreadParams {
  conversation_id: string
  message_id: string
  status: string
  message: string
}
